import './ChangeProfilePasswordForm.scss';
import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import notify from 'devextreme/ui/notify';
import { ValidationRule } from 'devextreme-react/common';
import Form, { Item, Label } from 'devextreme-react/form';
import { ValidatorRef, ValidatorTypes } from 'devextreme-react/validator';
import { FormPopup } from '../../utils/form-popup/FormPopup';
import { PasswordTextBox } from '../password-text-box/PasswordTextBox';
import { changePassword } from '../../../api/auth';

export const ChangeProfilePasswordForm = ({ visible, setVisible, email }) => {
  const confirmField = useRef<ValidatorRef>(null);
  const [confirmedPassword, setConfirmedPassword] = useState('');
  const [newPasswordValid, setNewPasswordValid] = useState(false);
  const [confirmedPasswordValid, setConfirmedPasswordValid] = useState(false);
  const [isSaveDisabled, setIsSaveDisabled] = useState(true);
  const [newPassword, setNewPassword] = useState('');
  const newPasswordRef = useRef('');
  const confirmPasswordValidators = useMemo((): ValidationRule[] => {
    return [{
      type: 'compare',
      message: 'Passwords do not match',
      comparisonTarget: () => newPassword,
    }];
  }, [newPassword]);

  useEffect(() => {
    const formValues = [newPassword, confirmedPassword];
    const validity = [newPasswordValid, confirmedPasswordValid];

    setIsSaveDisabled(
      formValues.some((value) => !value) ||
      validity.some((value) => !value)
    );
  }, [
    confirmedPassword,
    newPasswordValid,
    confirmedPasswordValid,
    email
  ]);

  const checkConfirm = useCallback(() => {
    confirmField.current?.instance().validate();
  }, []);

  const onConfirmedPasswordValidated = useCallback((e: ValidatorTypes.ValidatedEvent) => {
    setConfirmedPasswordValid(!!e.isValid);
  }, []);

  const onNewPasswordValidated = useCallback((e: ValidatorTypes.ValidatedEvent) => {
    setNewPasswordValid(!!e.isValid);
  }, []);

  const onNewPasswordChange = useCallback((value) => {
    setNewPassword(value);
    newPasswordRef.current = value;
    checkConfirm();
  }, [checkConfirm]);

  const saveNewPassword = useCallback(async() => {
    const password = newPasswordRef.current; // Get latest password
    if (!password || !email) {
      notify('Password or email is missing', 'error', 2000);
      return;
    }
    try {
      const result = await changePassword(password, email);
      if (!result.isOk) {
        notify(result.message, 'error', 2000);
        return;
      }
      notify({ message: 'Password Changed', position: { at: 'bottom center', my: 'bottom center' } }, 'success');
    } catch (error) {
      notify('An error occurred while changing the password', 'error', 2000);
    }
  }, [email]);

  return <FormPopup
    title='Change Password'
    visible={visible}
    width={360}
    wrapperAttr={{ class: 'change-profile-password-popup' }}
    isSaveDisabled={isSaveDisabled}
    onSave={saveNewPassword}
    setVisible={setVisible}
  >
    <Form id='form'
      labelMode='outside'
      showColonAfterLabel
      labelLocation='top'>
      <Item>
        <Label text='Password' />
        <PasswordTextBox
          value={newPassword}
          placeholder='Password'
          onValueChange={onNewPasswordChange}
          onValueValidated={onNewPasswordValidated}
        />
      </Item>

      <Item>
        <Label text='Confirm Password' />
        <PasswordTextBox
          ref={confirmField}
          value={confirmedPassword}
          placeholder='Confirm Password'
          validators={confirmPasswordValidators}
          onValueChange={setConfirmedPassword}
          onValueValidated={onConfirmedPasswordValidated}
        />
      </Item>
    </Form>
  </FormPopup>;
};
